import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Confetti from "react-confetti";
import { useSubscription } from '../../hooks/useSubscription';
import { UpgradeModal } from '../SubscriptionComponents';
import NumberLineJumpGame from './NumberLineJumpGame';
import FractionPizzaGame from './FractionPizzaGame';
import MathBingoGame from './MathBingoGame';
import MoneyShopGame from './MoneyShopGame';
import TimeClockGame from './TimeClockGame';
import GeometryBuilderGame from './GeometryBuilderGame';
import ChartMakerGame from './ChartMakerGame';
import MultiplicationRaceGame from './MultiplicationRaceGame';
import AlphabetAdventureGame from './AlphabetAdventureGame';
import SyllableSorterGame from './SyllableSorterGame';
import RhymeTimeGame from './RhymeTimeGame';
import SentenceBuilderGame from './SentenceBuilderGame';
import VowelConsonantGame from './VowelConsonantGame';
import StoryScrambleGame from './StoryScrambleGame';
import WordFamilyTreeGame from './WordFamilyTreeGame';
import CapitalLetterDetectiveGame from './CapitalLetterDetectiveGame';
import SoundSafariGame from './SoundSafariGame';
import TongueTwisterChallengeGame from './TongueTwisterChallengeGame';
import MinimalPairsMemoryGame from './MinimalPairsMemoryGame';
import BreathingDragonGame from './BreathingDragonGame';
import SyllableClapGame from './SyllableClapGame';
import SoundPositionFinderGame from './SoundPositionFinderGame';
import SightWordBingoGame from './SightWordBingoGame';
import ReadingComprehensionGame from './ReadingComprehensionGame';
import WordHuntGame from './WordHuntGame';
import CharacterDetectiveGame from './CharacterDetectiveGame';
import PunctuationPoliceGame from './PunctuationPoliceGame';
import SpeedReadingRacerGame from './SpeedReadingRacerGame';
import StoryMathGame from './StoryMathGame';
import WordNumberPuzzleGame from './WordNumberPuzzleGame';
import AIStoryCoWriterGame from './AIStoryCoWriterGame';
import MathArtGeneratorGame from './MathArtGeneratorGame';
import VirtualScienceLabGame from './VirtualScienceLabGame';
import EmotionCharadesGame from './EmotionCharadesGame';
import RecipeMathChefGame from './RecipeMathChefGame';
import GeographyTreasureHuntGame from './GeographyTreasureHuntGame';
import AnimalHabitatGame from './AnimalHabitatGame';
import WeatherStationGame from './WeatherStationGame';
import RhythmGame from './RhythmGame';
import NoteNameGame from './NoteNameGame';
import SimonSaysGame from './SimonSaysGame';
import ShapeYogaGame from './ShapeYogaGame';
import VeterinarianClinicGame from './VeterinarianClinicGame';
import StoreCashierGame from './StoreCashierGame';
import TeacherHelperGame from './TeacherHelperGame';
import TimeTravelHistoryGame from './TimeTravelHistoryGame';
import AICodingBlocksGame from './AICodingBlocksGame';
import DreamBuilderGame from './DreamBuilderGame';
import MysteryMathDetectiveGame from './MysteryMathDetectiveGame';
import DebateArenaGame from './DebateArenaGame';
import GameResultScreen from '../Gamification/GameResultScreen';
import KuciuStalasGame from './KuciuStalasGame';

// Layouts
import GameLayout from './Layouts/GameLayout';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

// ========== PATTERN MATCHING COMPONENT ==========
const PatternMatchingGame = ({ gameData, score, setScore, completeGame, playSound }) => {
  const [gameState, setGameState] = useState('memorize');
  const [userPattern, setUserPattern] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [shuffledOptions, setShuffledOptions] = useState([]);

  useEffect(() => {
    if (gameData?.options) {
      setShuffledOptions([...gameData.options].sort(() => Math.random() - 0.5));
    }
  }, [gameData]);

  useEffect(() => {
    let timer;
    if (gameState === 'memorize') {
      timer = setTimeout(() => {
        setGameState('recall');
        if (navigator.vibrate) navigator.vibrate(100);
      }, 4000);
    }
    return () => timer && clearTimeout(timer);
  }, [gameState]);

  const selectItem = (item) => {
    if (gameState !== 'recall' || feedback) return;
    const newPattern = [...userPattern, item];
    setUserPattern(newPattern);
    if (navigator.vibrate) navigator.vibrate(20);

    if (newPattern.length === gameData.pattern.length) {
      let isCorrect = true;
      for (let i = 0; i < newPattern.length; i++) {
        if (String(newPattern[i].id) !== String(gameData.pattern[i].id)) {
          isCorrect = false;
          break;
        }
      }

      setFeedback(isCorrect ? 'correct' : 'wrong');
      if (isCorrect) {
        playSound?.('correct');
        const points = gameData.points_per_pattern || 40;
        const newScore = score + points;
        setScore(newScore);
        triggerHaptic([100, 50, 100]);
        setTimeout(() => completeGame(newScore), 3500);
      } else {
        playSound?.('wrong');
        triggerHaptic([50, 50, 50]);
        setTimeout(() => {
          setUserPattern([]);
          setFeedback('');
          setGameState('memorize');
        }, 3000);
      }
    }
  };

  if (!gameData?.pattern || !gameData?.options) return null;

  const hud = (
    <div className="premium-glass bg-indigo-900 rounded-b-3xl p-4 md:p-8 border-b-4 border-indigo-400 shadow-2xl relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.05)_0%,transparent_70%)] pointer-events-none" />
      <div className="text-center relative z-10">
        <div className="text-4xl md:text-6xl mb-2 animate-float">
          {gameState === 'memorize' ? '🧠' : feedback === 'correct' ? '🌟' : feedback === 'wrong' ? '❌' : '🎯'}
        </div>
        <h3 className="text-xl md:text-3xl font-black text-white tracking-tighter uppercase">
          {gameState === 'memorize' ? 'Įsimink!' :
            feedback === 'correct' ? 'Tiksliai!' :
              feedback === 'wrong' ? 'Bandyk dar kartą!' : 'Atkartok Seką!'}
        </h3>
      </div>
    </div>
  );

  const choices = (
    <div className="space-y-6">
      <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-10 shadow-2xl border-white/40 bg-white/40 backdrop-blur-xl">
        <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.4em] mb-8 text-center">Pasirinkimų Bankas</h4>
        <div className="flex justify-center gap-4 flex-wrap">
          {shuffledOptions.map(item => (
            <button
              key={item.id}
              onClick={() => selectItem(item)}
              disabled={gameState !== 'recall' || feedback}
              className={`
                premium-button-3d w-24 h-24 md:w-32 md:h-32 rounded-lg md:rounded-[2.5rem] text-3xl md:text-6xl flex items-center justify-center transition-all duration-300
                ${gameState === 'recall' && !feedback
                  ? 'bg-white hover:scale-110 active:scale-95 border-slate-50'
                  : 'opacity-20 grayscale cursor-default'}
              `}
            >
              {item.emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const targets = (
    <div className="flex justify-center items-center gap-3 flex-wrap p-2">
      {gameData.pattern.map((item, index) => {
        const userItem = userPattern[index];
        const isFilled = userItem || gameState === 'memorize';

        return (
          <div key={index} className={`
            w-16 h-16 md:w-24 md:h-24 rounded-2xl flex items-center justify-center text-xl md:text-4xl transition-all duration-500 border-4 shadow-lg
            ${gameState === 'memorize' ? 'bg-white/10 border-white/20 animate-pop-in' :
              feedback === 'correct' ? 'bg-emerald-500/20 border-emerald-400 scale-110' :
                feedback === 'wrong' ? 'bg-rose-500/20 border-rose-400 animate-shake' :
                  isFilled ? 'bg-indigo-500/20 border-indigo-400' : 'bg-black/20 border-white/5'}
          `} style={{ animationDelay: `${index * 0.1}s` }}>
            {gameState === 'memorize' ? item.emoji : (userItem?.emoji || '')}
          </div>
        );
      })}
    </div>
  );

  return (
    <DragDropLayout
      hud={hud}
      choices={choices}
      targets={targets}
    />
  );
};
// ========== WORD BUILDER COMPONENT ==========
const WordBuilderGame = ({ gameData, score, setScore, completeGame, playSound }) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState([]);
  const [usedLetterIndices, setUsedLetterIndices] = useState([]);
  const [shuffledLetters, setShuffledLetters] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (gameData?.words?.[currentWordIndex]) {
      const word = gameData.words[currentWordIndex].word;
      const letters = word.split('');
      const shuffled = [...letters].sort(() => Math.random() - 0.5);
      setShuffledLetters(shuffled);
      setUserAnswer([]);
      setUsedLetterIndices([]);
      setShowHint(false);
      setFeedback(null);
    }
  }, [currentWordIndex, gameData]);

  const currentWord = gameData?.words?.[currentWordIndex];
  if (!currentWord) return null;

  const addLetter = (letter, index) => {
    if (feedback || usedLetterIndices.includes(index)) return;
    setUserAnswer([...userAnswer, letter]);
    setUsedLetterIndices([...usedLetterIndices, index]);
    if (navigator.vibrate) navigator.vibrate(20);
  };

  const removeLetter = () => {
    if (feedback || userAnswer.length === 0) return;
    setUserAnswer(userAnswer.slice(0, -1));
    setUsedLetterIndices(usedLetterIndices.slice(0, -1));
    if (navigator.vibrate) navigator.vibrate(10);
  };

  const checkAnswer = () => {
    const answer = userAnswer.join('');
    const correct = answer.toLowerCase() === currentWord.word.toLowerCase();

    if (correct) {
      setFeedback('correct');
      playSound?.('correct');
      triggerHaptic([100, 50, 100]);
      const points = gameData.points_per_word || 30;
      const newScore = score + points;
      setScore(newScore);

      setTimeout(() => {
        if (currentWordIndex + 1 >= gameData.words.length) {
          completeGame(newScore);
        } else {
          setCurrentWordIndex(currentWordIndex + 1);
        }
      }, 3000);
    } else {
      setFeedback('wrong');
      playSound?.('wrong');
      triggerHaptic([50, 50, 50]);
      setTimeout(() => {
        setUserAnswer([]);
        setUsedLetterIndices([]);
        setFeedback(null);
      }, 1500);
    }
  };

  const hud = (
    <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-white/80 backdrop-blur-md">
      <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 rounded-full blur-[80px] pointer-events-none"></div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
            📝
          </div>
          <div>
            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
              Žodžių Statytojas
            </h3>
            <p className="text-slate-500 font-bold">Sudėk raides į teisingą žodį!</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-slate-50 px-6 py-2 rounded-2xl font-black text-slate-800 border-2 border-slate-100">
            Žodis {currentWordIndex + 1}/{gameData.words.length}
          </div>
          <button onClick={() => setShowHint(!showHint)} className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl transition-all shadow-lg ${showHint ? 'bg-amber-500 text-white' : 'bg-white text-amber-500 border-2 border-amber-50'}`}>
            💡
          </button>
        </div>
      </div>
    </div>
  );

  const choices = (
    <div className="space-y-6">
      <div className="premium-glass rounded-[3.5rem] p-4 md:p-10 shadow-2xl border-white/60 bg-indigo-900">
        <h4 className="text-xs font-black text-indigo-400 uppercase tracking-[0.4em] mb-8 text-center">Raidžių Bankas</h4>
        <div className="flex justify-center gap-3 flex-wrap">
          {shuffledLetters.map((letter, idx) => (
            <button
              key={idx}
              onClick={() => addLetter(letter, idx)}
              disabled={usedLetterIndices.includes(idx) || !!feedback}
              className={`
                  premium-button-3d w-16 h-16 md:w-20 md:h-20 rounded-[1.5rem] text-lg md:text-4xl font-black flex items-center justify-center transition-all duration-300
                  ${usedLetterIndices.includes(idx) ? 'opacity-20 bg-indigo-950 text-indigo-800 border-transparent scale-90' : 'bg-white text-indigo-900 border-slate-100'}
                `}
            >
              {letter}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const targets = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
      {/* Visual Cue */}
      <div className="premium-glass rounded-2xl md:rounded-[3rem] p-4 md:p-8 shadow-inner border-white/40 bg-white/30 backdrop-blur-md flex flex-col items-center justify-center">
        <div className={`text-6xl md:text-9xl drop-shadow-2xl transition-all duration-700 ${feedback === 'correct' ? 'scale-110' : feedback === 'wrong' ? 'animate-shake' : 'animate-float'}`}>
          {currentWord.emoji}
        </div>
        {showHint && (
          <div className="mt-4 bg-amber-50 rounded-2xl p-3 border-2 border-amber-100 text-amber-900 font-bold italic animate-pop-in text-center text-sm">
            "{currentWord.hint}"
          </div>
        )}
      </div>

      {/* Workspace */}
      <div className="space-y-4">
        <div className="flex justify-center gap-2 mb-4 flex-wrap min-h-[60px] items-center">
          {currentWord.word.split('').map((_, idx) => (
            <div key={idx} className={`
                w-12 h-16 md:w-16 md:h-20 rounded-xl flex items-center justify-center text-lg md:text-3xl font-black transition-all duration-300 border-4 shadow-md
                ${feedback === 'correct' ? 'bg-emerald-500 border-emerald-400 text-white scale-105' :
                feedback === 'wrong' ? 'bg-rose-500 border-rose-400 text-white animate-shake' :
                  userAnswer[idx] ? 'bg-white border-teal-400 text-teal-600' : 'bg-slate-50/50 border-slate-200 text-transparent'}
              `}>
              {userAnswer[idx] || ''}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2">
          <button onClick={removeLetter} disabled={userAnswer.length === 0 || !!feedback} className="premium-button-3d py-4 bg-slate-100 text-slate-500 rounded-xl font-black text-xs disabled:opacity-40 uppercase">Trinti</button>
          <button onClick={checkAnswer} disabled={userAnswer.length !== currentWord.word.length || !!feedback} className="col-span-2 premium-button-3d py-4 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl font-black text-sm shadow-xl disabled:opacity-40 uppercase">Tikrinti ✓</button>
        </div>
      </div>
    </div>
  );

  return (
    <DragDropLayout
      hud={hud}
      choices={choices}
      targets={targets}
    />
  );
};

// ========== SEQUENCE BUILDER COMPONENT (PREMIUM) ==========
// const SequenceBuilderGame = ({ gameData, score, setScore, completeGame, playSound }) => {
//   const [itemsToPlace, setItemsToPlace] = useState([]);
//   const [sequenceSlots, setSequenceSlots] = useState([]);
//   const [baseItems, setBaseItems] = useState([]);
//   const [draggedItemSeq, setDraggedItemSeq] = useState(null);
//   const [isWrong, setIsWrong] = useState(false);

//   useEffect(() => {
//     const itemsRaw = gameData?.items || gameData?.elements || gameData?.sequence || [];
//     if (itemsRaw && Array.isArray(itemsRaw) && itemsRaw.length > 0) {
//       const safeItems = itemsRaw.map((item, idx) => ({
//         ...item,
//         id: item.id || `item-${idx}`,
//         text: item.text || item.value || item.display || '',
//         order: item.order || (idx + 1)
//       }));
//       const safe = [...safeItems];
//       setBaseItems(safe);
//       setItemsToPlace([...safe].sort(() => Math.random() - 0.5));
//       setSequenceSlots(new Array(safe.length).fill(null));
//       setIsWrong(false);
//     }
//   }, [gameData]);

//   const handleDragStart = (e, item) => {
//     setDraggedItemSeq(item);
//     e.dataTransfer.setData("item", JSON.stringify(item));
//   };
//   const handleDragOver = (e) => { e.preventDefault(); };
//   const handleDragEnd = () => { setDraggedItemSeq(null); };

//   const handleDrop = (e, index) => {
//     e.preventDefault();
//     const itemData = e.dataTransfer.getData("item");
//     if (!itemData) return;
//     const item = JSON.parse(itemData);
//     setDraggedItemSeq(null);
//     if (sequenceSlots[index]) return;
//     setItemsToPlace(prev => prev.filter(i => i.id !== item.id));
//     const newSlots = [...sequenceSlots];
//     newSlots[index] = item;
//     setSequenceSlots(newSlots);
//   };

//   const removeSlotItem = (index) => {
//     const itemToRemove = sequenceSlots[index];
//     if (!itemToRemove) return;
//     const newSlots = [...sequenceSlots];
//     newSlots[index] = null;
//     setSequenceSlots(newSlots);
//     setItemsToPlace(prev => [...prev, itemToRemove]);
//   };

//   const checkSequence = () => {
//     const isCorrect = sequenceSlots.every((item, index) => {
//       if (!item) return false;
//       const expectedOrder = index + 1;
//       return parseInt(item.order) === expectedOrder || parseInt(item.text) === expectedOrder || parseInt(item.value) === expectedOrder;
//     });
//     if (isCorrect) {
//       playSound('correct');
//       const points = (gameData.points_per_item || 10) * (baseItems.length || 0);
//       setScore(prevScore => prevScore + points);
//       setTimeout(() => { completeGame(score + points); }, 3000);
//     } else {
//       playSound('wrong');
//       setIsWrong(true);
//       setTimeout(() => {
//         setIsWrong(false);
//         setItemsToPlace([...baseItems].sort(() => Math.random() - 0.5));
//         setSequenceSlots(new Array(baseItems.length).fill(null));
//       }, 2500);
//     }
//   };

//   return (
//     <div className="space-y-3 md:space-y-8 font-sans">
//       {/* Premium Instructions Header */}
//       <div className="bg-gradient-to-tl from-yellow-300 via-orange-400 to-red-500 rounded-[35px] p-3 md:p-8 shadow-2xl relative overflow-hidden border border-white/40">
//         <div className="absolute top-0 right-0 w-80 h-80 bg-white/20 rounded-full blur-[80px] pointer-events-none"></div>
//         <div className="flex flex-col md:flex-row items-center gap-4 relative z-10">
//           <div className="text-2xl md:text-6xl animate-bounce drop-shadow-lg">🚂</div>
//           <div className="text-center md:text-left">
//             <h3 className="text-base md:text-3xl font-black text-white drop-shadow-md uppercase tracking-wider">
//               {gameData?.title || 'Sekos Iššūkis!'}
//             </h3>
//             <div className="mt-2 bg-white/60 backdrop-blur-md inline-block px-5 py-2 rounded-2xl shadow-sm border border-white/30">
//               <p className="text-red-900 text-lg font-black">
//                 📝 {gameData?.instructions || gameData?.description || 'Išdėliokite elementus teisinga tvarka!'}
//               </p>
//             </div>
//           </div>
//         </div>

//         {/* Source Items */}
//         <div className="bg-white/20 p-2 md:p-6 rounded-[25px] border border-white/30 shadow-inner backdrop-blur-md min-h-[120px] flex justify-center flex-wrap gap-4 mt-6">
//           {itemsToPlace.map(item => (
//             <div
//               key={item.id}
//               draggable
//               onDragStart={(e) => handleDragStart(e, item)}
//               onDragEnd={handleDragEnd}
//               className={`bg-white/95 backdrop-blur-sm px-6 py-4 rounded-2xl shadow-[0_10px_20px_rgba(0,0,0,0.15)] cursor-grab active:cursor-grabbing hover:scale-110 transition-all duration-300 border-b-4 border-orange-200 flex flex-col items-center justify-center min-w-[100px] select-none ${draggedItemSeq?.id === item.id ? 'opacity-40 scale-90' : 'animate-float'}`}
//             >
//               {item.emoji && <div className="text-lg md:text-4xl mb-1 drop-shadow-md">{item.emoji}</div>}
//               {item.text && <div className="text-base font-black text-orange-900 tracking-wide">{item.text}</div>}
//             </div>
//           ))}
//           {itemsToPlace.length === 0 && (
//             <div className="text-xl text-white font-black w-full text-center py-4 drop-shadow-md animate-pulse">
//               Visi elementai tavo rankose! 🔍
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Target Sequence Slots */}
//       <div className={`bg-gradient-to-br from-slate-100 to-indigo-50 rounded-[35px] p-3 md:p-8 shadow-2xl border-4 transition-colors duration-500 overflow-x-auto ${isWrong ? 'border-red-400' : 'border-white'}`}>
//         <h3 className="text-2xl font-black text-indigo-300 mb-6 text-center tracking-widest uppercase">Teisinga Seka:</h3>

//         <div className="flex justify-center flex-nowrap items-center gap-2 md:gap-3 min-w-max pb-4 px-2 mx-auto">
//           {sequenceSlots.map((item, index) => (
//             <React.Fragment key={`slot-wrap-${index}`}>
//               <div
//                 onDrop={(e) => handleDrop(e, index)}
//                 onDragOver={handleDragOver}
//                 onClick={() => removeSlotItem(index)}
//                 className={`relative w-24 h-32 md:w-32 md:h-40 rounded-3xl flex flex-col items-center justify-center transition-all duration-300 ${item ? 'bg-white border-b-8 border-indigo-200 shadow-[0_15px_30px_rgba(0,0,0,0.1)] cursor-pointer hover:-translate-y-2 group' : 'border-4 border-dashed border-indigo-200 bg-white/50 hover:bg-white hover:border-indigo-400 shadow-inner'}`}
//               >
//                 <div className="absolute -top-4 -left-4 w-8 h-8 rounded-full bg-indigo-500 text-white flex justify-center items-center font-black text-sm shadow-lg z-10">
//                   {index + 1}
//                 </div>
//                 {item ? (
//                   <>
//                     {item.emoji && <div className="text-lg md:text-4xl md:text-xl md:text-5xl mb-1 drop-shadow-md group-hover:scale-110 transition-transform">{item.emoji}</div>}
//                     {item.text && <div className="font-black text-indigo-900 text-xs md:text-sm text-center px-1 leading-tight tracking-wide">{item.text}</div>}
//                     <div className="absolute top-1 right-1 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow-sm font-bold">✕</div>
//                   </>
//                 ) : (
//                   <div className="text-base md:text-3xl text-indigo-100">📥</div>
//                 )}
//               </div>
//               {index < sequenceSlots.length - 1 && (
//                 <div className="text-2xl md:text-base md:text-3xl text-indigo-200 font-bold select-none">➔</div>
//               )}
//             </React.Fragment>
//           ))}
//         </div>

//         <div className="text-center mt-6 min-h-[60px]">
//           {itemsToPlace.length === 0 && !isWrong && (
//             <button onClick={checkSequence} className="px-10 py-5 bg-gradient-to-r from-emerald-400 to-teal-500 text-white rounded-2xl hover:from-emerald-500 hover:to-teal-600 transition-all duration-300 font-black text-xl shadow-[0_10px_30px_rgba(52,211,153,0.4)] active:scale-95 animate-bounce">
//               ✓ Pabaigti seką
//             </button>
//           )}
//           {isWrong && (
//             <div className="text-2xl font-black text-red-500 animate-pulse">❌ Bandykite dar kartą! Grąžinu elementus...</div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };
// import { useEffect, useState } from "react";
// import Confetti from "react-confetti";

// ========== SEQUENCE BUILDER (PREMIUM) ==========
const SequenceBuilderGame = ({ gameData, score, setScore, completeGame, playSound }) => {
  const [itemsToPlace, setItemsToPlace] = useState([]);
  const [sequenceSlots, setSequenceSlots] = useState([]);
  const [baseItems, setBaseItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const itemsRaw = gameData?.items || [];
    if (itemsRaw.length > 0) {
      const safeItems = itemsRaw.map((item, idx) => ({
        ...item,
        id: item.id || `item-${idx}`,
        order: item.order || idx + 1,
      }));
      setBaseItems(safeItems);
      setItemsToPlace([...safeItems].sort(() => Math.random() - 0.5));
      setSequenceSlots(new Array(safeItems.length).fill(null));
      setSelectedItem(null);
      setFeedback(null);
    }
  }, [gameData]);

  const handleItemClick = (item) => {
    if (feedback) return;
    setSelectedItem((prev) => (prev?.id === item.id ? null : item));
    if (navigator.vibrate) navigator.vibrate(20);
  };

  const handleSlotClick = (index) => {
    if (feedback) return;

    if (sequenceSlots[index]) {
      const itemToRemove = sequenceSlots[index];
      const newSlots = [...sequenceSlots];
      newSlots[index] = null;
      setSequenceSlots(newSlots);
      setItemsToPlace((prev) => [...prev, itemToRemove]);
      if (navigator.vibrate) navigator.vibrate(20);
      return;
    }

    if (!selectedItem) return;

    const newSlots = [...sequenceSlots];
    newSlots[index] = selectedItem;
    setSequenceSlots(newSlots);
    setItemsToPlace((prev) => prev.filter((i) => i.id !== selectedItem.id));
    setSelectedItem(null);
    if (navigator.vibrate) navigator.vibrate(30);
  };

  const checkSequence = () => {
    const isCorrect = sequenceSlots.every((item, index) => item && parseInt(item.order) === index + 1);

    if (isCorrect) {
      setFeedback('correct');
      playSound?.("correct");
      triggerHaptic([100, 50, 100]);
      const points = (gameData.points_per_item || 10) * baseItems.length;
      const newScore = score + points;
      setScore(newScore);
      setTimeout(() => completeGame(newScore), 3000);
    } else {
      setFeedback('wrong');
      playSound?.("wrong");
      triggerHaptic([50, 50, 50]);
      setTimeout(() => {
        setItemsToPlace([...baseItems].sort(() => Math.random() - 0.5));
        setSequenceSlots(new Array(baseItems.length).fill(null));
        setFeedback(null);
      }, 1500);
    }
  };

  const hud = (
    <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
            🔄
          </div>
          <div>
            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
              Seka ir Tvarka
            </h3>
            <p className="text-slate-500 font-bold">Sudėliok korteles teisinga eilės tvarka!</p>
          </div>
        </div>
        <div className="bg-indigo-600 text-white px-6 py-2 rounded-2xl font-black shadow-lg">
          Išdėliota: {baseItems.length - itemsToPlace.length}/{baseItems.length}
        </div>
      </div>
    </div>
  );

  const choices = (
    <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col">
      <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.4em] mb-8 text-center">Kortelių Kaladė</h4>
      <div className="flex flex-wrap gap-4 justify-center min-h-[200px] items-center">
        {itemsToPlace.map(item => (
          <button
            key={item.id}
            onClick={() => handleItemClick(item)}
            className={`
                premium-button-3d bg-white p-2 md:p-6 rounded-[2rem] flex flex-col items-center gap-2 transition-all duration-300
                ${selectedItem?.id === item.id ? "ring-4 ring-indigo-400 scale-110 shadow-indigo-100" : "hover:scale-105 active:scale-95"}
              `}
          >
            <div className="text-xl md:text-5xl drop-shadow-sm">{item.emoji}</div>
            <div className="font-black text-slate-700 text-sm">{item.text}</div>
          </button>
        ))}
        {itemsToPlace.length === 0 && (
          <div className="text-emerald-500 font-black text-xl animate-pulse">Visos kortelės savo vietose! ✓</div>
        )}
      </div>
    </div>
  );

  const targets = (
    <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/40 bg-slate-900 relative">
      <div className={`
         grid grid-cols-2 sm:grid-cols-3 md:grid-cols-1 gap-4 transition-all duration-500
         ${feedback === 'wrong' ? 'animate-shake' : ''}
       `}>
        {sequenceSlots.map((item, idx) => (
          <button
            key={idx}
            onClick={() => handleSlotClick(idx)}
            className={`
                aspect-square md:aspect-video rounded-lg md:rounded-[2.5rem] flex flex-col items-center justify-center gap-2 transition-all duration-500 border-4 relative overflow-hidden
                ${item ? 'bg-white border-white scale-105 shadow-xl' : 'bg-white/5 border-dashed border-white/10 hover:border-white/30'}
              `}
          >
            <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-indigo-500/20 text-indigo-200 flex items-center justify-center text-xs font-black border border-white/10">{idx + 1}</div>
            {item ? (
              <>
                <div className="text-xl md:text-5xl animate-pop-in">{item.emoji}</div>
                <div className="text-[10px] font-black text-slate-500 uppercase tracking-tighter">{item.text}</div>
              </>
            ) : (
              <div className="text-base md:text-3xl text-white/10">📥</div>
            )}
          </button>
        ))}
      </div>

      {itemsToPlace.length === 0 && !feedback && (
        <div className="mt-8 animate-pop-in">
          <button onClick={checkSequence} className="premium-button-3d w-full py-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-[2rem] font-black text-2xl shadow-xl shadow-indigo-500/20 uppercase tracking-widest">
            Tikrinti Seką ✓
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <DragDropLayout
        hud={hud}
        choices={choices}
        targets={targets}
      />

      {/* Global Results Overlay */}
      {feedback && (
        <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
          <div className={`
                max-w-md w-full p-4 md:p-16 rounded-2xl md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[12px] text-center
                ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
            `}>
            <div className="text-xl md:text-5xl md:text-[120px] mb-8 animate-bounce">
              {feedback === 'correct' ? '🌟' : '🧐'}
            </div>
            <h4 className={`text-lg md:text-4xl font-black uppercase tracking-tighter mb-4 ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
              {feedback === 'correct' ? 'Tobula!' : 'Beveik!'}
            </h4>
            <p className="text-slate-500 font-bold text-xl italic">
              {feedback === 'correct' ? 'Seka sudėliota be klaidų!' : 'Pabandyk dar kartą...'}
            </p>
          </div>
        </div>
      )}
    </>
  );
};



const MathEquationGame = ({ gameData, score, setScore, completeGame, playSound }) => {
  const [currentEquationIndex, setCurrentEquationIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const currentEquation = gameData?.equations?.[currentEquationIndex];

  const handleAnswer = (option) => {
    if (selectedAnswer || feedback) return;
    setSelectedAnswer(option);
    const isCorrect = String(option) === String(currentEquation.answer);

    if (isCorrect) {
      setFeedback('correct');
      playSound?.('correct');
      triggerHaptic(100);
      const points = gameData.points_per_correct || 20;
      const newScore = score + points;
      setScore(newScore);

      setTimeout(() => {
        if (currentEquationIndex + 1 < gameData.equations.length) {
          setCurrentEquationIndex(currentEquationIndex + 1);
          setSelectedAnswer(null);
          setFeedback(null);
        } else {
          completeGame(newScore);
        }
      }, 3000);
    } else {
      setFeedback('wrong');
      playSound?.('wrong');
      triggerHaptic([50, 50, 50]);
      setTimeout(() => {
        setSelectedAnswer(null);
        setFeedback(null);
      }, 1500);
    }
  };

  if (!currentEquation) return null;

  const hud = (
    <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-white/80 backdrop-blur-md">
      <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
            🔢
          </div>
          <div>
            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
              Skaičių Laboratorija
            </h3>
            <p className="text-slate-500 font-bold">Išspręsk lygtį ir pasirink teisingą atsakymą!</p>
          </div>
        </div>
        <div className="bg-emerald-600 text-white px-6 py-2 rounded-2xl font-black shadow-lg">
          Taškai: {score}
        </div>
      </div>
    </div>
  );

  const choices = (
    <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-16 text-center shadow-2xl relative overflow-hidden border-white/60 bg-slate-900 flex flex-col items-center justify-center min-h-[300px]">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(52,211,153,0.1)_0%,transparent_70%)] pointer-events-none" />

      <div className={`
         text-6xl md:text-[8rem] font-black tracking-tighter leading-none mb-4 transition-all duration-700 font-mono
         ${feedback === 'correct' ? 'text-emerald-400 scale-110 drop-shadow-[0_0_40px_rgba(52,211,153,0.6)]' :
          feedback === 'wrong' ? 'text-rose-400 animate-shake' : 'text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.2)]'}
      `}>
        {currentEquation.question} = <span className="opacity-20">?</span>
      </div>

      <div className="mt-8 flex gap-3">
        <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
        <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)] [animation-delay:0.2s]" />
        <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)] [animation-delay:0.4s]" />
      </div>
    </div>
  );

  const targets = (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:p-6">
      {currentEquation.options.map((option, index) => {
        const isSelected = selectedAnswer === option;

        let btnClass = 'premium-button-3d bg-white text-slate-700 border-slate-100 hover:scale-110';
        if (isSelected && feedback === 'correct') btnClass = 'bg-emerald-500 text-white border-emerald-600 scale-110 shadow-emerald-200 shadow-2xl';
        else if (isSelected && feedback === 'wrong') btnClass = 'bg-rose-500 text-white border-rose-600 animate-shake shadow-rose-200 shadow-2xl';
        else if (feedback && String(option) === String(currentEquation.answer)) btnClass = 'bg-emerald-500/50 text-white border-emerald-400';

        return (
          <button
            key={index}
            onClick={() => handleAnswer(option)}
            disabled={!!selectedAnswer}
            className={`
              rounded-lg md:rounded-[2.5rem] p-4 md:p-10 text-2xl md:text-6xl font-black transition-all duration-300 transform disabled:cursor-default select-none
              ${btnClass}
            `}
          >
            {option}
          </button>
        );
      })}
    </div>
  );

  return (
    <DragDropLayout
      hud={hud}
      choices={choices}
      targets={targets}
    />
  );
};

const ShapesSortingGame = ({ gameData, completedItems, draggedItem, setDraggedItem, handleDrop }) => {
  const hud = (
    <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-white/80 backdrop-blur-md">
      <div className="absolute top-0 right-0 w-80 h-80 bg-violet-500/5 rounded-full blur-[80px] pointer-events-none"></div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
            📐
          </div>
          <div>
            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
              Formų Rūšiuotojas
            </h3>
            <p className="text-slate-500 font-bold">Sudėk figūras į joms skirtas vietas!</p>
          </div>
        </div>
        <div className="bg-violet-600 text-white px-6 py-2 rounded-2xl font-black shadow-lg">
          Liko: {(gameData.items || []).length - completedItems.length}
        </div>
      </div>
    </div>
  );

  const choices = (
    <div className="flex flex-wrap justify-center gap-4 md:p-6 bg-slate-50/50 p-4 md:p-10 rounded-xl md:rounded-[3rem] border-4 border-dashed border-slate-100 shadow-inner min-h-[200px] items-center">
      {(gameData.items || [])
        .filter(item => !completedItems.includes(item.id))
        .map((item) => (
          <button
            key={item.id}
            onClick={() => { setDraggedItem(item); triggerHaptic(30); }}
            className={`
              premium-glass flex flex-col items-center p-2 md:p-6 rounded-lg md:rounded-[2.5rem] transition-all duration-500 border-white/60
              ${draggedItem?.id === item.id
                ? 'bg-white shadow-2xl ring-8 ring-violet-100 scale-125 -translate-y-6'
                : 'hover:bg-white/80 active:scale-95 shadow-lg animate-float'}
            `}
          >
            <div className="text-5xl md:text-7xl mb-2">{item.emoji}</div>
            <div className="text-xs md:text-sm font-black text-slate-500 uppercase tracking-tighter">{item.name}</div>
          </button>
        ))}
      {(gameData.items || []).filter(item => !completedItems.includes(item.id)).length === 0 && (
        <div className="text-base md:text-3xl text-violet-500 font-black animate-pop-in">Visos formos savo vietose! 🚀</div>
      )}
    </div>
  );

  const targets = (
    <div className="grid grid-cols-2 md:grid-cols-1 gap-3 p-4 md:p-6">
      {(gameData.categories || []).map((cat) => (
        <button
          key={cat.id}
          onClick={() => { if (draggedItem) { handleDrop(cat.id, 'shape'); triggerHaptic(50); } }}
          className={`
            premium-button-3d group bg-white rounded-2xl md:rounded-[2.5rem] p-4 md:p-8 flex flex-col items-center transition-all duration-500 min-h-[250px] relative overflow-hidden border-slate-100
            ${draggedItem ? 'animate-pulse-subtle ring-4 ring-violet-100 ring-offset-4' : ''}
          `}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-violet-50/50 to-transparent pointer-events-none" />
          <div className="text-4xl md:text-6xl mb-4 transition-transform group-hover:scale-110 drop-shadow-2xl relative z-10">{cat.emoji}</div>
          <h4 className="text-sm md:text-xl font-black text-slate-800 tracking-tighter uppercase mb-4 relative z-10">{cat.name}</h4>

          <div className="mt-auto flex flex-wrap gap-2 justify-center w-full bg-slate-50 p-2 md:p-4 rounded-xl md:rounded-2xl min-h-[80px] shadow-inner border border-slate-100 relative z-10">
            {(gameData.items || [])
              .filter(item => String(item.category).toLowerCase() === String(cat.id).toLowerCase() && completedItems.includes(item.id))
              .map(item => (
                <div key={item.id} className="text-2xl md:text-4xl animate-pop-in drop-shadow-lg">
                  {item.emoji}
                </div>
              ))}
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <DragDropLayout
      hud={hud}
      choices={choices}
      targets={targets}
    />
  );
};

const ColorsMatchingGame = ({ gameData, completedItems, draggedItem, setDraggedItem, handleDrop }) => {
  const hud = (
    <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-white/80 backdrop-blur-md">
      <div className="absolute top-0 right-0 w-80 h-80 bg-rose-500/5 rounded-full blur-[80px] pointer-events-none"></div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
            🎨
          </div>
          <div>
            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
              Spalvų Magija
            </h3>
            <p className="text-slate-500 font-bold">Surūšiuok daiktus pagal jų spalvas!</p>
          </div>
        </div>
        <div className="bg-rose-600 text-white px-6 py-2 rounded-2xl font-black shadow-lg">
          Liko: {(gameData.items || []).length - completedItems.length}
        </div>
      </div>
    </div>
  );

  const choices = (
    <div className="flex flex-wrap justify-center gap-4 md:p-6 bg-slate-50/50 p-4 md:p-10 rounded-xl md:rounded-[3rem] border-4 border-dashed border-slate-100 shadow-inner min-h-[200px] items-center">
      {(gameData.items || [])
        .filter(item => !completedItems.includes(item.id))
        .map((item) => (
          <button
            key={item.id}
            onClick={() => { setDraggedItem(item); triggerHaptic(30); }}
            className={`
              premium-glass flex flex-col items-center p-2 md:p-6 rounded-lg md:rounded-[2.5rem] transition-all duration-500 border-white/60
              ${draggedItem?.id === item.id
                ? 'bg-white shadow-2xl ring-8 ring-rose-100 scale-125 -translate-y-6'
                : 'hover:bg-white/80 active:scale-95 shadow-lg animate-float'}
            `}
          >
            <div className="text-5xl md:text-7xl mb-2">{item.emoji}</div>
            <div className="text-xs md:text-sm font-black text-slate-500 uppercase tracking-tighter">{item.name}</div>
          </button>
        ))}
      {(gameData.items || []).filter(item => !completedItems.includes(item.id)).length === 0 && (
        <div className="text-base md:text-3xl text-rose-500 font-black animate-pop-in">Viskas surūšiuota! ✨</div>
      )}
    </div>
  );

  const targets = (
    <div className="grid grid-cols-2 md:grid-cols-1 gap-3 p-4 md:p-6">
      {(gameData.colors || []).map((color) => (
        <button
          key={color.id}
          onClick={() => { if (draggedItem) { handleDrop(color.id, 'color'); triggerHaptic(50); } }}
          className={`
            premium-button-3d group bg-white rounded-2xl md:rounded-[2.5rem] p-4 md:p-8 flex flex-col items-center transition-all duration-500 min-h-[250px] relative overflow-hidden border-slate-100
            ${draggedItem ? 'animate-pulse-subtle ring-4 ring-rose-100 ring-offset-4' : ''}
          `}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-rose-50/50 to-transparent pointer-events-none" />
          <div
            className="w-16 h-16 md:w-24 md:h-24 rounded-full mb-6 transition-transform group-hover:scale-110 shadow-2xl relative z-10 border-4 border-white"
            style={{ backgroundColor: color.hex }}
          />
          <h4 className="text-sm md:text-xl font-black text-slate-800 tracking-tighter uppercase mb-4 relative z-10">{color.name}</h4>

          <div className="mt-auto flex flex-wrap gap-2 justify-center w-full bg-slate-50 p-2 md:p-4 rounded-xl md:rounded-2xl min-h-[80px] shadow-inner border border-slate-100 relative z-10">
            {(gameData.items || [])
              .filter(item => String(item.color).toLowerCase() === String(color.id).toLowerCase() && completedItems.includes(item.id))
              .map(item => (
                <div key={item.id} className="text-2xl md:text-4xl animate-pop-in drop-shadow-lg">
                  {item.emoji}
                </div>
              ))}
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <DragDropLayout
      hud={hud}
      choices={choices}
      targets={targets}
    />
  );
};

const NumberMatchingGame = ({ gameData, completedItems, draggedItem, setDraggedItem, handleDrop }) => {
  const hud = (
    <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-white/80 backdrop-blur-md">
      <div className="absolute top-0 right-0 w-80 h-80 bg-emerald-500/5 rounded-full blur-[80px] pointer-events-none"></div>
      <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
            🔢
          </div>
          <div>
            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
              Skaičių Detektyvas
            </h3>
            <p className="text-slate-500 font-bold">Sujunk skaičius su teisingais kiekiais!</p>
          </div>
        </div>
        <div className="bg-emerald-600 text-white px-6 py-2 rounded-2xl font-black shadow-lg">
          Liko: {(gameData.items || []).length - completedItems.length}
        </div>
      </div>
    </div>
  );

  const choices = (
    <div className="flex flex-wrap justify-center gap-4 md:p-6 bg-slate-50/50 p-4 md:p-10 rounded-xl md:rounded-[3rem] border-4 border-dashed border-slate-100 shadow-inner min-h-[200px] items-center">
      {(gameData.items || [])
        .filter(item => !completedItems.includes(item.id))
        .map((item) => (
          <button
            key={item.id}
            onClick={() => { setDraggedItem(item); triggerHaptic(30); }}
            className={`
              premium-glass flex flex-col items-center p-2 md:p-6 rounded-lg md:rounded-[2.5rem] transition-all duration-500 border-white/60
              ${draggedItem?.id === item.id
                ? 'bg-white shadow-2xl ring-8 ring-emerald-100 scale-125 -translate-y-6'
                : 'hover:bg-white/80 active:scale-95 shadow-lg animate-float'}
            `}
          >
            <div className="text-5xl md:text-7xl mb-2">{item.emoji_group || item.emoji}</div>
            <div className="text-xs md:text-sm font-black text-slate-500 uppercase tracking-tighter">{item.description || item.name}</div>
          </button>
        ))}
    </div>
  );

  const targets = (
    <div className="grid grid-cols-2 md:grid-cols-1 gap-3 p-4 md:p-6">
      {(gameData.numbers || []).map((num) => (
        <button
          key={num}
          onClick={() => { if (draggedItem) { handleDrop(num, 'number'); triggerHaptic(50); } }} className={`
            premium-button-3d group bg-white rounded-2xl md:rounded-[2.5rem] p-4 md:p-8 flex flex-col items-center transition-all duration-500 min-h-[250px] relative overflow-hidden border-slate-100
            ${draggedItem ? 'animate-pulse-subtle ring-4 ring-emerald-100 ring-offset-4' : ''}
          `}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-transparent pointer-events-none" />
          <div className="text-5xl md:text-7xl font-black text-emerald-600 mb-4 transition-transform group-hover:scale-110 drop-shadow-lg relative z-10">{num}</div>

          <div className="mt-auto flex flex-wrap gap-2 justify-center w-full bg-slate-50 p-2 md:p-4 rounded-xl md:rounded-2xl min-h-[80px] shadow-inner border border-slate-100 relative z-10">
            {(gameData.items || [])
              .filter(item => item.number === num && completedItems.includes(item.id))
              .map(item => (
                <div key={item.id} className="text-2xl md:text-4xl animate-pop-in drop-shadow-lg">
                  {item.emoji_group || item.emoji}
                </div>
              ))}
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <DragDropLayout
      hud={hud}
      choices={choices}
      targets={targets}
    />
  );
};


// ========== STORY SEQUENCE (PREMIUM) ==========
const StorySequenceGame = ({ gameData, score, setScore, completeGame, playSound }) => {
  const [sentencesToPlace, setSentencesToPlace] = useState([]);
  const [storySlots, setStorySlots] = useState([]);
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (gameData?.sentences) {
      setSentencesToPlace([...gameData.sentences].sort(() => Math.random() - 0.5));
      setStorySlots(new Array(gameData.sentences.length).fill(null));
      setFeedback(null);
    }
  }, [gameData]);

  const handleSentenceClick = (sentence) => {
    if (feedback) return;
    setSelectedSentence(selectedSentence?.id === sentence.id ? null : sentence);
    if (navigator.vibrate) navigator.vibrate(20);
  };

  const handleSlotClick = (index) => {
    if (feedback) return;

    if (storySlots[index]) {
      const removed = storySlots[index];
      const newSlots = [...storySlots];
      newSlots[index] = null;
      setStorySlots(newSlots);
      setSentencesToPlace(prev => [...prev, removed]);
      if (navigator.vibrate) navigator.vibrate(20);
      return;
    }

    if (!selectedSentence) return;

    const newSlots = [...storySlots];
    newSlots[index] = selectedSentence;
    setStorySlots(newSlots);
    setSentencesToPlace(prev => prev.filter(s => s.id !== selectedSentence.id));
    setSelectedSentence(null);
    playSound?.('click');
    if (navigator.vibrate) navigator.vibrate(30);
  };

  const checkSequence = () => {
    const isCorrect = storySlots.every((item, index) => item && item.order === index + 1);
    if (isCorrect) {
      setFeedback('correct');
      playSound('correct');
      if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
      const points = (gameData.points_per_sentence || 10) * (gameData.sentences?.length || 0);
      const newScore = score + points;
      setScore(newScore);
      setTimeout(() => completeGame(newScore), 3500);
    } else {
      setFeedback('wrong');
      playSound('wrong');
      if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
      setTimeout(() => {
        setSentencesToPlace([...gameData.sentences].sort(() => Math.random() - 0.5));
        setStorySlots(new Array(gameData.sentences.length).fill(null));
        setFeedback(null);
      }, 1500);
    }
  };

  return (
    <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
      {/* Premium Header */}
      <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 shadow-2xl relative overflow-hidden border-white/40">
        <div className="absolute top-0 right-0 w-80 h-80 bg-amber-500/5 rounded-full blur-[80px] pointer-events-none"></div>
        <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
              📚
            </div>
            <div>
              <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
                Istorijos Kūrėjas
              </h3>
              <p className="text-slate-500 font-bold">Sudėliok įvykius teisinga tvarka!</p>
            </div>
          </div>
          <div className="bg-amber-600 text-white px-6 py-2 rounded-2xl font-black shadow-lg">
            Sutvarkyta: {gameData.sentences.length - sentencesToPlace.length}/{gameData.sentences.length}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:p-8 items-start">
        {/* Sentences Column */}
        <div className="lg:col-span-5 space-y-4">
          <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.4em] mb-4 px-6 text-center lg:text-left">Įvykių Sąrašas</h4>
          <div className="flex flex-col gap-4">
            {sentencesToPlace.map(sentence => (
              <button
                key={sentence.id}
                onClick={() => handleSentenceClick(sentence)}
                className={`
                    premium-button-3d bg-white px-8 py-6 rounded-lg md:rounded-[2.5rem] text-left border-slate-50 transition-all duration-300
                    ${selectedSentence?.id === sentence.id ? 'ring-8 ring-amber-100 scale-105 shadow-amber-500/10' : 'hover:-translate-y-1 active:scale-95'}
                  `}
              >
                <p className="text-xl font-black text-slate-700 leading-snug">📖 {sentence.text}</p>
              </button>
            ))}
            {sentencesToPlace.length === 0 && (
              <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-dashed border-4 border-slate-100 bg-white/40">
                <p className="text-emerald-500 font-black text-2xl animate-pulse">Istorija paruošta! ✅</p>
              </div>
            )}
          </div>
        </div>

        {/* Timeline Column */}
        <div className="lg:col-span-7 space-y-4">
          <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.4em] mb-4 px-6 text-center lg:text-left">Laiko Juosta</h4>
          <div className={`
              premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/50 flex flex-col gap-4 min-h-[500px] relative overflow-hidden bg-white/40 backdrop-blur-xl
              ${feedback === 'wrong' ? 'animate-shake border-rose-200' : ''}
            `}>
            <div className="absolute top-0 right-0 p-4 md:p-12 opacity-5 text-2xl md:text-6xl md:text-[200px] font-black select-none pointer-events-none">📜</div>

            {storySlots.map((sentence, index) => (
              <button
                key={index}
                onClick={() => handleSlotClick(index)}
                className={`
                    w-full min-h-[100px] rounded-xl md:rounded-[3rem] flex items-center gap-2 md:p-6 px-8 py-6 transition-all duration-500 relative border-4
                    ${sentence
                    ? 'bg-white shadow-xl border-white scale-[1.02] hover:border-rose-200'
                    : 'border-dashed border-slate-200 bg-slate-50/30 hover:bg-white hover:border-indigo-300'}
                  `}
              >
                <div className="w-14 h-14 rounded-full bg-slate-900 text-white font-black flex items-center justify-center shrink-0 border-4 border-white shadow-lg text-xl">{index + 1}</div>
                {sentence
                  ? <span className="text-slate-800 font-black text-xl flex-1 text-left leading-tight animate-pop-in">{sentence.text}</span>
                  : <span className="text-slate-300 font-black text-xl italic">Paliesk čia...</span>
                }
                {sentence && (
                  <div className="text-rose-400 font-black text-2xl ml-4 opacity-40">×</div>
                )}
              </button>
            ))}

            {storySlots.every(s => s !== null) && !feedback && (
              <div className="pt-8 mt-auto animate-pop-in relative z-10">
                <button
                  onClick={checkSequence}
                  className="premium-button-3d w-full py-3 md:py-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg md:rounded-[2.5rem] font-black text-2xl shadow-2xl shadow-emerald-500/20 uppercase tracking-widest"
                >
                  Baigti Istoriją ✓
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const UniversalAIGames = ({ user, category = 'all' }) => {
  const { usage, checkLimit, updateUsage } = useSubscription();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [requiredLevel, setRequiredLevel] = useState(1);

  const GAME_ACCESS_LEVELS = {
    // Level 0: Free
    'shapes_sorting': 0,
    'colors_matching': 0,
    'number_matching': 0,
    'word_builder': 0,
    'sequence_builder': 0,
    'pattern_matching': 0,
    'math_equation': 0,
    'number_line_jump': 0,
    'alphabet_adventure': 0,
    'vowel_consonant': 0,
    'sound_safari': 0,
    'syllable_sorter': 0,

    // Level 1: Basic
    'story_sequence': 1,
    'fraction_pizza': 1,
    'math_bingo': 1,
    'money_shop': 1,
    'time_clock': 1,
    'geometry_builder': 1,
    'chart_maker': 1,
    'multiplication_race': 1,
    'rhyme_time': 1,
    'sentence_builder': 1,
    'story_scramble': 1,
    'word_family_tree': 1,
    'capital_letter_detective': 1,
    'sight_word_bingo': 1,
    'reading_comprehension': 1,
    'word_hunt': 1,
    'character_detective': 1,
    'punctuation_police': 1,
    'speed_reading_racer': 1,
    'story_math': 1,
    'word_number_puzzle': 1,
    'emotion_charades': 1,
    'geography_treasure_hunt': 1,
    'animal_habitat': 1,
    'weather_station': 1,
    'rhythm_maker': 1,
    'note_name': 1,
    'simon_says': 1,
    'shape_yoga': 1,
    'minimal_pairs_memory': 1,
    'breathing_dragon': 1,
    'syllable_clap': 1,
    'sound_position_finder': 1,

    // Level 2: Premium / Family
    'ai_story_cowriter': 2,
    'math_art': 2,
    'science_lab': 2,
    'recipe_math_chef': 2,
    'veterinarian_clinic': 2,
    'store_cashier': 2,
    'teacher_helper': 2,
    'time_travel_history': 2,
    'ai_coding_blocks': 2,
    'dream_builder': 2,
    'mystery_math_detective': 2,
    'debate_arena': 2,
    'kuciu_stalas': 2,
    'tongue_twister_challenge': 2,
    'custom': 2
  };

  const isLocked = (gameType) => {
    const userAccessLevel = usage?.access_level || 0;
    const required = GAME_ACCESS_LEVELS[gameType] || 0;
    return userAccessLevel < required;
  };
  const location = useLocation();
  const navigate = useNavigate();
  const [availableGames, setAvailableGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [timeSpent, setTimeSpent] = useState(0);

  // Game state
  const [draggedItem, setDraggedItem] = useState(null);
  const [completedItems, setCompletedItems] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [globalFeedback, setGlobalFeedback] = useState(null);

  useEffect(() => {
    loadAvailableGames();
  }, [category]);

  // Auto-start game from React Router state or URL parameter
  useEffect(() => {
    const stateGame = location.state?.autoStartGame;
    const urlGame = new URLSearchParams(location.search).get('game');
    const gameToStart = stateGame || urlGame;

    if (!gameToStart) return;

    console.log("Auto-starting game:", gameToStart);
    generateGame(gameToStart);

    // Clear navigation state / URL parameter to prevent re-triggering on refresh
    if (stateGame) {
      window.history.replaceState({}, document.title);
    } else if (urlGame) {
      const cleanUrl = `${location.pathname}`;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [location.state, location.search, location.pathname]);

  // Timer
  useEffect(() => {
    if (playing) {
      const timer = setInterval(() => setTimeSpent(t => t + 1), 1000);
      return () => clearInterval(timer);
    }
  }, [playing]);

  const loadAvailableGames = async () => {
    try {
      // Mock data - replace with real API
      const allGames = [
        {
          type: "shapes_sorting",
          name: "Figūrų rūšiavimas",
          icon: "🔷",
          description: "Rūšiuok figūras pagal formą",
          color: "from-pink-500 to-purple-500",
          category: "simple"
        },
        {
          type: "colors_matching",
          name: "Spalvų derinimas",
          icon: "🎨",
          description: "Sujunk objektus su spalvomis",
          color: "from-orange-500 to-red-500",
          category: "simple"
        },
        {
          type: "number_matching",
          name: "Skaičių atitikimas",
          icon: "🔢",
          description: "Sujunk skaičius su objektais",
          color: "from-blue-500 to-purple-500",
          category: "simple"
        },
        {
          type: "word_builder",
          name: "Žodžių sudarymas",
          icon: "📝",
          description: "Sudėk raides į žodžius",
          color: "from-green-500 to-teal-500",
          category: "advanced"
        },
        {
          type: "sequence_builder",
          name: "Sekos kūrimas",
          icon: "🔢",
          description: "Sudėk elementus teisinga tvarka",
          color: "from-indigo-500 to-blue-500",
          category: "advanced"
        },
        {
          type: "pattern_matching",
          name: "Šablonų derinimas",
          icon: "🎭",
          description: "Įsimink ir pakartok šabloną",
          color: "from-pink-500 to-red-500",
          category: "advanced"
        },
        {
          type: "story_sequence",
          name: "Pasakojimo seka",
          icon: "📖",
          description: "Sudėk pasakojimą teisinga tvarka",
          color: "from-purple-500 to-pink-500",
          category: "advanced"
        },
        {
          type: "math_equation",
          name: "Matematinės lygtys",
          icon: "➕",
          description: "Išspręsk matematikos uždavinius",
          color: "from-yellow-500 to-orange-500",
          category: "advanced"
        },
        {
          type: "number_line_jump",
          name: "Šokinėjimas ant skaičių tiesės",
          icon: "🦘",
          description: "Šokinėk ant skaičių tiesės",
          color: "from-green-500 to-emerald-500",
          category: "simple"
        },
        {
          type: "fraction_pizza",
          name: "Trupmenos su Pica",
          icon: "🍕",
          description: "Skirstyk picas į dalis, mokykis trupmenas",
          color: "from-orange-500 to-red-500",
          category: "simple"
        },
        {
          type: "math_bingo",
          name: "Matematikos Bingo",
          icon: "🎲",
          description: "Bingo su matematikos uždaviniais",
          color: "from-purple-500 to-pink-500",
          category: "advanced"
        },
        {
          type: "money_shop",
          name: "Pinigų Parduotuvė",
          icon: "💰",
          description: "Suskaičiuok pinigus ir pirk daiktus",
          color: "from-green-500 to-yellow-500",
          category: "simple"
        },
        {
          type: "time_clock",
          name: "Laikrodžio Meistras",
          icon: "⏰",
          description: "Nustatyk laikrodį pagal pateiktą laiką",
          color: "from-blue-500 to-indigo-500",
          category: "simple"
        },
        {
          type: "geometry_builder",
          name: "Geometrijos Statytojas",
          icon: "📐",
          description: "Sukurk figūras iš mažesnių dalių",
          color: "from-pink-500 to-purple-500",
          category: "advanced"
        },
        {
          type: "chart_maker",
          name: "Grafikų Kūrėjas",
          icon: "📊",
          description: "Sukurk grafiką pagal duomenis",
          color: "from-blue-500 to-green-500",
          category: "advanced"
        },
        {
          type: "multiplication_race",
          name: "Dauginimo Lenktynės",
          icon: "🏁",
          description: "Užpildyk dauginimo lentelę greičiau nei laikas",
          color: "from-orange-500 to-red-500",
          category: "advanced"
        },
        {
          type: "alphabet_adventure",
          name: "Abėcėlės Nuotykis",
          icon: "🔤",
          description: "Rask visas raides abėcėlės tvarka",
          color: "from-green-500 to-teal-500",
          category: "simple"
        },
        {
          type: "syllable_sorter",
          name: "Skiemenų Rūšiuotojas",
          icon: "🎵",
          description: "Skirstyk žodžius pagal skiemenų skaičių",
          color: "from-pink-500 to-rose-500",
          category: "simple"
        },
        {
          type: "rhyme_time",
          name: "Rimų Laikas",
          icon: "🎤",
          description: "Sujunk žodžius, kurie rimas",
          color: "from-purple-500 to-indigo-500",
          category: "simple"
        },
        {
          type: "sentence_builder",
          name: "Sakinių Sudarymas",
          icon: "🏗️",
          description: "Sudėk sakinį iš žodžių",
          color: "from-green-500 to-blue-500",
          category: "advanced"
        },
        {
          type: "vowel_consonant",
          name: "Balsės vs Priebalsės",
          icon: "🅰️",
          description: "Rūšiuok raides į balses ir priebalses",
          color: "from-red-500 to-blue-500",
          category: "simple"
        },
        {
          type: "story_scramble",
          name: "Pasakos Sudėliojimas",
          icon: "📚",
          description: "Sudėk pasakos dalis teisinga tvarka",
          color: "from-purple-500 to-pink-500",
          category: "advanced"
        },
        {
          type: "word_family_tree",
          name: "Žodžių Šeimos Medis",
          icon: "🌳",
          description: "Grupuok giminingas žodžių formas",
          color: "from-green-600 to-teal-600",
          category: "advanced"
        },
        {
          type: "capital_letter_detective",
          name: "Didžiųjų Raidžių Detektyvas",
          icon: "🔍",
          description: "Rask žodžius, kurie turi būti su didžiąja raide",
          color: "from-blue-600 to-purple-600",
          category: "advanced"
        },
        {
          type: "sound_safari",
          name: "Garsų Safari",
          icon: "🦁",
          description: "Rask gyvūnus pagal garsą (logopedija)",
          color: "from-orange-500 to-yellow-500",
          category: "simple"
        },
        {
          type: "tongue_twister_challenge",
          name: "Žodžių Laužiklių Iššūkis",
          icon: "👅",
          description: "Kartok sunkius žodžių junginius (logopedija)",
          color: "from-purple-500 to-pink-500",
          category: "simple"
        },
        {
          type: "minimal_pairs_memory",
          name: "Minimalių Porų Atmintis",
          icon: "🧠",
          description: "Sujunk žodžius, kurie skiriasi vienu garsu",
          color: "from-blue-500 to-indigo-500",
          category: "simple"
        },
        {
          type: "breathing_dragon",
          name: "Kvėpuojantis Drakonas",
          icon: "🐉",
          description: "Kvėpavimo pratimai su drakonų vizualizacija",
          color: "from-green-500 to-teal-500",
          category: "simple"
        },
        {
          type: "syllable_clap",
          name: "Skiemenų Plojimas",
          icon: "👏",
          description: "Plok pagal skiemenų skaičių",
          color: "from-purple-500 to-pink-500",
          category: "simple"
        },
        {
          type: "sound_position_finder",
          name: "Garso Detektyvas",
          icon: "📍",
          description: "Kurioje vietoje girdisi garsas?",
          color: "from-orange-500 to-red-500",
          category: "simple"
        },
        {
          type: "sight_word_bingo",
          name: "Žodžių Bingo",
          icon: "👀",
          description: "Surask žodžius bingo kortelėje",
          color: "from-blue-500 to-cyan-500",
          category: "simple"
        },
        {
          type: "reading_comprehension",
          name: "Pasakų Kelionė",
          icon: "🗺️",
          description: "Skaityk pasakas ir atsakyk į klausimus",
          color: "from-yellow-500 to-amber-500",
          category: "simple"
        },
        {
          type: "word_hunt",
          name: "Žodžių Medžioklė",
          icon: "🔦",
          description: "Rask paslėptus žodžius tekste",
          color: "from-green-500 to-emerald-500",
          category: "simple"
        },
        {
          type: "character_detective",
          name: "Veikėjų Detektyvas",
          icon: "🕵️",
          description: "Atspėk, kas yra kas pagal aprašymą",
          color: "from-purple-500 to-fuchsia-500",
          category: "simple"
        },
        {
          type: "punctuation_police",
          name: "Skyrybos Policija",
          icon: "👮",
          description: "Ištaisyk skyrybą sakiniuose",
          color: "from-blue-500 to-indigo-500",
          category: "simple"
        },
        {
          type: "speed_reading_racer",
          name: "Greito Skaitymo Lenktynės",
          icon: "🏎️",
          description: "Skaityk greitai ir tiksliai",
          color: "from-red-500 to-orange-500",
          category: "simple"
        },
        {
          type: "story_math",
          name: "Pasakų Matematika",
          icon: "📖➕",
          description: "Spręsk matematikos uždavinius pasakose",
          color: "from-purple-500 to-pink-500",
          category: "advanced"
        },
        {
          type: "word_number_puzzle",
          name: "Žodžių ir Skaičių Galvosūkis",
          icon: "🧩",
          description: "Mišrūs uždaviniai - žodžiai ir matematika",
          color: "from-teal-500 to-cyan-500",
          category: "advanced"
        },
        {
          type: "ai_story_cowriter",
          name: "AI Pasakų Rašytojas",
          icon: "✍️",
          description: "Rašyk pasaką kartu su AI",
          color: "from-indigo-500 to-purple-500",
          category: "advanced"
        },
        {
          type: "math_art",
          name: "Matematikos Meno Kūrėjas",
          icon: "🎨➕",
          description: "Spręsk uždavinius ir sukurk meną",
          color: "from-pink-500 to-orange-500",
          category: "advanced"
        },
        {
          type: "science_lab",
          name: "Virtuali Mokslo Laboratorija",
          icon: "🧪",
          description: "Atlik mokslo eksperimentus",
          color: "from-green-500 to-blue-500",
          category: "advanced"
        },
        {
          type: "emotion_charades",
          name: "Emocijų Charados",
          icon: "😊😢😡",
          description: "Atpažink emocijas iš emoji sekų",
          color: "from-yellow-500 to-pink-500",
          category: "simple"
        },
        {
          type: "recipe_math_chef",
          name: "Receptų Matematikos Šefas",
          icon: "👨‍🍳",
          description: "Gamink ir skaičiuok ingredientus",
          color: "from-orange-500 to-red-500",
          category: "advanced"
        },
        {
          type: "geography_treasure_hunt",
          name: "Geografijos Lobių Medžioklė",
          icon: "🗺️",
          description: "Rask miestus pagal užuominas",
          color: "from-blue-500 to-green-500",
          category: "simple"
        },
        {
          type: "animal_habitat",
          name: "Gyvūnų Buveinės",
          icon: "🦁🌲",
          description: "Sujunk gyvūnus su jų namais",
          color: "from-green-500 to-emerald-500",
          category: "simple"
        },
        {
          type: "weather_station",
          name: "Orų Stotis",
          icon: "☀️🌧️",
          description: "Prognozuok orus pagal ženklus",
          color: "from-sky-500 to-indigo-500",
          category: "advanced"
        },
        {
          type: "rhythm_maker",
          name: "Ritmo Kūrėjas",
          icon: "🥁",
          description: "Pakartok ritmą",
          color: "from-purple-500 to-pink-500",
          category: "simple"
        },
        {
          type: "note_name",
          name: "Natų Pavadinimai",
          icon: "🎹",
          description: "Mokykis natų pavadinimų",
          color: "from-gray-700 to-gray-900",
          category: "simple"
        },
        {
          type: "simon_says",
          name: "Simonas Sako",
          icon: "💃",
          description: "Vykdyk komandas",
          color: "from-yellow-400 to-orange-500",
          category: "simple"
        },
        {
          type: "shape_yoga",
          name: "Figūrų Joga",
          icon: "🧘",
          description: "Sukurk figūras kūnu",
          color: "from-green-400 to-teal-500",
          category: "simple"
        },
        {
          type: "veterinarian_clinic",
          name: "Veterinarijos Klinika",
          icon: "🏥",
          description: "Gydyk gyvūnus",
          color: "from-red-400 to-pink-500",
          category: "simple"
        },
        {
          type: "store_cashier",
          name: "Parduotuvės Kasininkas",
          icon: "🛒",
          description: "Skaičiuok pinigus",
          color: "from-blue-500 to-indigo-600",
          category: "simple"
        },
        {
          type: "teacher_helper",
          name: "Mokytojo Padėjėjas",
          icon: "👩‍🏫",
          description: "Padėk mokytojui",
          color: "from-purple-500 to-pink-500",
          category: "simple"
        },
        {
          type: "time_travel_history",
          name: "Laiko Kelionė per Istoriją",
          icon: "⏰",
          description: "Keliauk per istoriją",
          color: "from-amber-500 to-orange-600",
          category: "simple"
        },
        {
          type: "ai_coding_blocks",
          name: "AI Programavimo Blokai",
          icon: "🤖",
          description: "Programuok robotą",
          color: "from-cyan-500 to-blue-600",
          category: "advanced"
        },
        {
          type: "dream_builder",
          name: "Svajonių Kūrėjas",
          icon: "🌙",
          description: "Sukurk savo pasaulį",
          color: "from-purple-400 via-pink-500 to-indigo-500",
          category: "advanced"
        },
        {
          type: "mystery_math_detective",
          name: "Paslapčių Matematikos Detektyvas",
          icon: "🔍",
          description: "Išspręsk paslaptis",
          color: "from-yellow-500 via-orange-500 to-red-500",
          category: "advanced"
        },
        {
          type: "debate_arena",
          name: "Debatų Arena",
          icon: "🎤",
          description: "Argumentuok savo poziciją",
          color: "from-blue-500 via-purple-500 to-pink-500",
          category: "advanced"
        },
        {
          type: "kuciu_stalas",
          name: "Kūčių Stalas",
          icon: "🎄",
          description: "Išdėstyk tradicinį Kūčių stalą",
          color: "from-red-600 via-green-600 to-red-600",
          category: "simple"
        }
      ];

      if (category === 'all') {
        setAvailableGames(allGames);
      } else {
        setAvailableGames(allGames.filter(g => g.category === category));
      }
    } catch (error) {
      console.error('Failed to load games:', error);
    }
  };

  const getGameParameters = (gameType, difficulty, user) => {
    const baseParams = {
      difficulty,
      age: user?.age,
      grade: user?.grade
    };
    switch (gameType) {
      case 'shapes_sorting':
        return { ...baseParams, item_count: 8 };
      case 'colors_matching':
        return { ...baseParams, item_count: 6 };
      case 'number_matching':
        return { ...baseParams, max_number: 10 };
      case 'word_builder':
        return { ...baseParams, word_count: 3 };
      case 'sequence_builder':
        return { ...baseParams, sequence_length: 5 };
      case 'pattern_matching':
        return { ...baseParams, pattern_length: 4 };
      case 'story_sequence':
        return { ...baseParams, sentence_count: 4 };
      case 'math_equation':
        return { ...baseParams, equation_count: 5 };
      case 'number_line_jump':
        return { ...baseParams, max_number: 20, jump_count: 5 };
      case 'fraction_pizza':
        return { ...baseParams, pizza_count: 4 };
      case 'math_bingo':
        return { ...baseParams, grid_size: 5 };
      case 'money_shop':
        return { ...baseParams, item_count: 5 };
      case 'time_clock':
        return { ...baseParams, time_count: 5 };
      case 'geometry_builder':
        return { ...baseParams, shape_count: 3 };
      case 'chart_maker':
        return { ...baseParams, chart_count: 3 };
      case 'multiplication_race':
        return { ...baseParams, grid_size: 5 };
      case 'alphabet_adventure':
        return { ...baseParams, letter_count: 10 };
      case 'syllable_sorter':
        return { ...baseParams, word_count: 12 };
      case 'rhyme_time':
        return { ...baseParams, pair_count: 5 };
      case 'sentence_builder':
        return { ...baseParams, sentence_count: 5 };
      case 'vowel_consonant':
        return { ...baseParams, letter_count: 12 };
      case 'story_scramble':
        return { ...baseParams, part_count: 4 };
      case 'word_family_tree':
        return { ...baseParams, family_count: 3 };
      case 'capital_letter_detective':
        return { ...baseParams, sentence_count: 5 };
      case 'sound_safari':
        return { ...baseParams, animal_count: 8 };
      case 'tongue_twister_challenge':
        return { ...baseParams, twister_count: 3 };
      case 'minimal_pairs_memory':
        return { ...baseParams, pair_count: 6 };
      case 'breathing_dragon':
        return { ...baseParams, exercise_count: 3 };
      case 'syllable_clap':
        return { ...baseParams, word_count: 10 };
      case 'sound_position_finder':
        return { ...baseParams, word_count: 10 };
      case 'sight_word_bingo':
        return { ...baseParams, grid_size: 3 };
      case 'reading_comprehension':
        return { ...baseParams, question_count: 3 };
      case 'word_hunt':
        return { ...baseParams, word_count: 5 };
      case 'character_detective':
        return { ...baseParams, character_count: 3 };
      case 'punctuation_police':
        return { ...baseParams, sentence_count: 5 };
      case 'speed_reading_racer':
        return { ...baseParams, question_count: 3 };
      case 'story_math':
        return { ...baseParams, problem_count: 3 };
      case 'word_number_puzzle':
        return { ...baseParams, clue_count: 4 };
      case 'math_art':
        return { ...baseParams, problem_count: 6 };
      case 'science_lab':
        return { ...baseParams, experiment_count: 5 };
      case 'emotion_charades':
        return { ...baseParams, sequence_count: 5 };
      case 'recipe_math_chef':
        return { ...baseParams, recipe_count: 3 };
      case 'geography_treasure_hunt':
        return { ...baseParams, location_count: 5 };
      case 'animal_habitat':
        return { ...baseParams, pair_count: 4 };
      case 'weather_station':
        return { ...baseParams, scenario_count: 3 };
      case 'rhythm_maker':
        return { ...baseParams, pattern_count: 3 };
      case 'note_name':
        return { ...baseParams, note_count: 5 };
      case 'simon_says':
        return { ...baseParams, round_count: 5 };
      case 'shape_yoga':
        return { ...baseParams, pose_count: 3 };
      case 'veterinarian_clinic':
        return { ...baseParams, case_count: 3 };
      case 'store_cashier':
        return { ...baseParams, scenario_count: 3 };
      case 'teacher_helper':
        return { ...baseParams, task_count: 5 };
      case 'time_travel_history':
        return { ...baseParams, era_count: 3 };
      case 'ai_coding_blocks':
        return { ...baseParams, level_count: 3 };
      case 'dream_builder':
        return { ...baseParams, elements: ['pilis', 'drakonas', 'vaivorykštė'] };
      case 'mystery_math_detective':
        return { ...baseParams, clue_count: 3 };
      case 'debate_arena':
        return { ...baseParams, topic: 'Ar šunys geresni už kates?' };
      case 'kuciu_stalas':
        return { ...baseParams, dish_count: 12 };
      default:
        return baseParams;
    }
  };

  const generateGame = async (gameType, difficulty = 'easy') => {
    if (isLocked(gameType)) {
      setRequiredLevel(GAME_ACCESS_LEVELS[gameType] || 1);
      setShowUpgradeModal(true);
      return;
    }

    if (!checkLimit()) {
      setShowUpgradeModal(true);
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');

      // Special handling for AI Story Co-Writer
      const endpoint = gameType === 'ai_story_cowriter'
        ? `${process.env.REACT_APP_API_URL}/api/v1/ai-games/story-cowriter/start`
        : `${process.env.REACT_APP_API_URL}/api/v1/ai-games/${gameType.replace(/_/g, '-')}`;

      console.info(`🚀 Generuojamas žaidimas: ${gameType} (${difficulty}) -> ${endpoint}`);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(getGameParameters(gameType, difficulty, user))
      });

      if (response.ok) {
        const data = await response.json();
        // DIAGNOSTIKA: Pažiūrėkime, ką realiai atsiunčia API
        console.log("Gauti žaidimo duomenys iš API:", data);

        // Update usage stats immediately to reflect token consumption
        if (data.usage_stats) {
          updateUsage(data.usage_stats);
        }

        setGameData(data.game_data);
        setPlaying(true);
        setScore(0);
        setTimeSpent(0);
        setCompletedItems([]);
        setUserProgress({});
      } else if (response.status === 401) {
        alert("Jūsų sesija pasibaigė. Prašome prisijungti iš naujo! 🔐");
        localStorage.removeItem('access_token');
        window.location.href = '/login'; // Or your login path
        setLoading(false);
        return;
      } else if (response.status === 403) {
        setRequiredLevel(GAME_ACCESS_LEVELS[gameType] || 1);
        setShowUpgradeModal(true);
        setLoading(false);
        return;
      } else if (response.status === 429) {
        const errorData = await response.json();
        alert(`AI limitai išnaudoti! ${errorData.detail?.message || 'Pagerink prenumeratą! 🚀'}`);
        setLoading(false);
        return;
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Server error' }));
        console.error('API Error:', response.status, errorData);
        alert(`Klaida generuojant žaidimą (${response.status}): ${errorData.detail?.message || errorData.detail || 'Bandykite dar kartą'}`);
        setLoading(false);
        return;
      }
    } catch (error) {
      console.error('Failed to generate game:', error);
      alert(`Nepavyko prisijungti prie serverio: ${error.message}`);
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (targetId, targetType) => {
    if (!draggedItem) return;

    const correct = checkAnswer(draggedItem, targetId, targetType);

    if (correct) {
      setCompletedItems([...completedItems, draggedItem.id]);
      setScore(score + (gameData.points_per_item || 10));
      playSound('correct'); // Automatic feedback via playSound

      const totalItems = gameData.items?.length || 0;
      if (completedItems.length + 1 >= totalItems) {
        completeGame();
      }
    } else {
      playSound('wrong'); // Automatic feedback via playSound
    }

    setDraggedItem(null);
  };

  const checkAnswer = (item, targetId) => {
    switch (gameData.game_type) {
      case 'shapes_sorting': {
        const iCat = String(item.category).toLowerCase().trim();
        const tId = String(targetId).toLowerCase().trim();
        if (iCat === tId) return true;
        const targetCategoryItem = gameData.categories?.find(c => String(c.id).toLowerCase().trim() === tId);
        if (targetCategoryItem && iCat === String(targetCategoryItem.name).toLowerCase().trim()) return true;
        return false;
      }
      case 'colors_matching': {
        const iCol = String(item.color).toLowerCase().trim();
        const tId = String(targetId).toLowerCase().trim();
        if (iCol === tId) return true;
        const targetColorItem = gameData.colors?.find(c => String(c.id).toLowerCase().trim() === tId);
        if (targetColorItem && iCol === String(targetColorItem.name).toLowerCase().trim()) return true;
        return false;
      }
      case 'number_matching':
        return parseInt(item.number) === parseInt(targetId);
      default:
        return false;
    }
  };

  const [showResult, setShowResult] = useState(false);
  const [resultData, setResultData] = useState({});

  const playSound = (type) => {
    // AUTOMATINIS GRĮŽTAMASIS RYŠYS:
    // Kai groja sėkmės ar klaidos garsas, automatiškai parodome overlay visuose žaidimuose.
    if (type === 'correct' || type === 'wrong') {
      setGlobalFeedback(type);
      setTimeout(() => setGlobalFeedback(null), 1200);
    }

    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      if (type === 'correct') {
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(500, audioCtx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1000, audioCtx.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.1);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.1);
      } else {
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(300, audioCtx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(100, audioCtx.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.3, audioCtx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.2);
        oscillator.start();
        oscillator.stop(audioCtx.currentTime + 0.2);
      }
    } catch (e) {
      console.log(`🎵 ${type} sound:`, e);
    }
  };

  const completeGame = async (finalScore) => {
    setPlaying(false);
    const scoreToSave = finalScore ?? score;

    // Numatyti duomenys lentelės rodymui, kol laukiame API atsakymo
    setResultData({
      score: scoreToSave,
      xpEarned: scoreToSave > 0 ? 10 : 0,
      levelUp: false,
      newLevel: 1,
      streak: null,
      achievements: []
    });
    setShowResult(true);

    // Išsaugome rezultatą į DB
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/dashboard/save-result`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          game_type: gameData.game_type,
          score: scoreToSave,
          difficulty: gameData.difficulty || 'easy',
          metadata: {
            title: gameData.title || gameData.name
          }
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.gamification) {
          // Atnaujiname rezultatų lentelę duomenimis iš backend'o
          setResultData(prev => ({
            ...prev,
            xpEarned: data.gamification.xp_earned || prev.xpEarned,
            levelUp: data.gamification.level_up,
            newLevel: data.gamification.level,
            streak: data.gamification.streak,
            achievements: data.gamification.new_achievements || []
          }));

          // [SVARBU] Siunčiame globalų įvykį, kad XP juosta ir kiti widget'ai atsinaujintų iškart
          const event = new CustomEvent('gamification-update', {
            detail: data.gamification
          });
          window.dispatchEvent(event);
        }
      } else if (response.status === 401) {
        // Silent or explicit log out if token expires during save
        console.warn("Session expired during save-result");
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    } catch (error) {
      console.error("Nepavyko išsaugoti rezultato:", error);
    }
  };

  const renderGameContent = () => {
    if (!gameData) return null;

    switch (gameData.game_type) {
      case 'shapes_sorting':
        return <ShapesSortingGame gameData={gameData} completedItems={completedItems} draggedItem={draggedItem} setDraggedItem={setDraggedItem} handleDrop={handleDrop} />;
      case 'colors_matching':
        return <ColorsMatchingGame gameData={gameData} completedItems={completedItems} draggedItem={draggedItem} setDraggedItem={setDraggedItem} handleDrop={handleDrop} />;
      case 'number_matching':
        return <NumberMatchingGame gameData={gameData} completedItems={completedItems} draggedItem={draggedItem} setDraggedItem={setDraggedItem} handleDrop={handleDrop} />;
      case 'word_builder':
        return <WordBuilderGame gameData={gameData} score={score} setScore={setScore} completeGame={completeGame} playSound={playSound} />;
      case 'sequence_builder':
        return <SequenceBuilderGame gameData={gameData} score={score} setScore={setScore} completeGame={completeGame} playSound={playSound} />;
      case 'pattern_matching':
        return <PatternMatchingGame gameData={gameData} score={score} setScore={setScore} completeGame={completeGame} playSound={playSound} />;
      case 'story_sequence':
        return <StorySequenceGame gameData={gameData} score={score} setScore={setScore} completeGame={completeGame} playSound={playSound} />;
      case 'math_equation':
        return <MathEquationGame
          gameData={gameData}
          score={score}
          setScore={setScore}
          completeGame={completeGame}
          playSound={playSound}
        />;
      case 'number_line_jump':
        return <NumberLineJumpGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'fraction_pizza':
        return <FractionPizzaGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'math_bingo':
        return <MathBingoGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'money_shop':
        return <MoneyShopGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'time_clock':
        return <TimeClockGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'geometry_builder':
        return <GeometryBuilderGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'chart_maker':
        return <ChartMakerGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'multiplication_race':
        return <MultiplicationRaceGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'alphabet_adventure':
        return <AlphabetAdventureGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'syllable_sorter':
        return <SyllableSorterGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'rhyme_time':
        return <RhymeTimeGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'sentence_builder':
        return <SentenceBuilderGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'vowel_consonant':
        return <VowelConsonantGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'story_scramble':
        return <StoryScrambleGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'word_family_tree':
        return <WordFamilyTreeGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'capital_letter_detective':
        return <CapitalLetterDetectiveGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'sound_safari':
        return <SoundSafariGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'tongue_twister_challenge':
        return <TongueTwisterChallengeGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'minimal_pairs_memory':
        return <MinimalPairsMemoryGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'breathing_dragon':
        return <BreathingDragonGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'syllable_clap':
        return <SyllableClapGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'sound_position_finder':
        return <SoundPositionFinderGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'sight_word_bingo':
        return <SightWordBingoGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'reading_comprehension':
        return <ReadingComprehensionGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'word_hunt':
        return <WordHuntGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'character_detective':
        return <CharacterDetectiveGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'punctuation_police':
        return <PunctuationPoliceGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'speed_reading_racer':
        return <SpeedReadingRacerGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'story_math':
        return <StoryMathGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'word_number_puzzle':
        return <WordNumberPuzzleGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'ai_story_cowriter':
        return <AIStoryCoWriterGame gameData={gameData} user={user} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'math_art':
        return <MathArtGeneratorGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'science_lab':
        return <VirtualScienceLabGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'emotion_charades':
        return (
          <EmotionCharadesGame
            gameData={gameData}
            onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }}
            onRegenerate={() => generateGame(gameData.game_type, gameData.difficulty)}
          />
        );
      case 'recipe_math_chef':
        return <RecipeMathChefGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'geography_treasure_hunt':
        return <GeographyTreasureHuntGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'animal_habitat':
        return <AnimalHabitatGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'weather_station':
        return <WeatherStationGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'rhythm_maker':
        return <RhythmGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'note_name':
        return <NoteNameGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'simon_says':
        return <SimonSaysGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'shape_yoga':
        return <ShapeYogaGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'veterinarian_clinic':
        return <VeterinarianClinicGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'store_cashier':
        return <StoreCashierGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'teacher_helper':
        return <TeacherHelperGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'time_travel_history':
        return <TimeTravelHistoryGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'ai_coding_blocks':
        return <AICodingBlocksGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'dream_builder':
        return <DreamBuilderGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'mystery_math_detective':
        return <MysteryMathDetectiveGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'debate_arena':
        return <DebateArenaGame gameData={gameData} onComplete={(finalScore) => { setScore(finalScore); completeGame(finalScore); }} />;
      case 'kuciu_stalas':
        return <KuciuStalasGame gameData={gameData} onAddScore={(points) => setScore(s => s + points)} onComplete={completeGame} playSound={playSound} />;
      default:
        return <div>Game type not implemented yet</div>;
    }
  };









  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-900 via-slate-900 to-black flex items-center justify-center p-4">
        <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-16 text-center max-w-lg w-full border-white/10 shadow-[0_0_100px_rgba(79,70,229,0.3)] animate-pop-in">
          <div className="relative inline-block mb-12">
            <div className="absolute inset-0 bg-indigo-500 blur-3xl opacity-40 animate-pulse"></div>
            <div className="text-[10rem] relative z-10 animate-float">🐼</div>
          </div>
          <h2 className="text-lg md:text-4xl font-black text-white mb-6 drop-shadow-lg tracking-tight">AI kuria tavo nuotykį...</h2>
          <div className="space-y-6">
            <div className="w-full bg-white/10 rounded-full h-4 overflow-hidden p-1 border border-white/5">
              <div className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full animate-xp-fill w-[60%]" />
            </div>
            <div className="flex justify-center gap-4">
              <div className="w-3 h-3 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
              <div className="w-3 h-3 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-3 h-3 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Playing state
  if (showResult) {
    return (
      <GameResultScreen
        score={resultData.score}
        xpEarned={resultData.xpEarned}
        levelUp={resultData.levelUp}
        newLevel={resultData.newLevel}
        streak={resultData.streak}
        achievements={resultData.achievements}
        gameName={gameData?.title || 'Žaidimas'}
        onContinue={() => {
          setShowResult(false);
          setGameData(null);
        }}
        onPlayAgain={() => {
          setShowResult(false);
          setScore(0);
          generateGame(gameData.game_type);
        }}
      />
    );
  }

  // Main game view
  if (playing && gameData) {
    const isInteractiveGame = [
      'sequence_builder',
      'shapes_sorting',
      'colors_matching',
      'number_matching',
      'story_sequence',
      'word_builder',
      'pattern_matching',
      'math_equation',
      'number_line_jump',
      'fraction_pizza',
      'math_bingo',
      'multiplication_race',
      'money_shop',
      'store_cashier',
      'chart_maker',
      'story_math',
      'recipe_math_chef',
      'alphabet_adventure',
      'syllable_sorter',
      'syllable_clap',
      'rhyme_time',
      'sentence_builder',
      'vowel_consonant',
      'word_family_tree',
      'capital_letter_detective',
      'punctuation_police',
      'sight_word_bingo',
      'story_scramble',
      'word_hunt',
      'reading_comprehension',
      'minimal_pairs_memory',
      'sound_position_finder',
      'character_detective',
      'weather_station',
      'virtual_science_lab',
      'geography_treasure_hunt',
      'animal_habitat',
      'geometry_builder',
      'shape_yoga',
      'emotion_charades',
      'note_name',
      'time_clock'
    ].includes(gameData.game_type);

    const onExit = async () => {
      if (score > 0) {
        try {
          const token = localStorage.getItem('access_token');
          await fetch(`${process.env.REACT_APP_API_URL}/api/v1/dashboard/save-result`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              game_type: gameData.game_type,
              score: score,
              difficulty: gameData.difficulty || 'easy',
              metadata: {
                title: gameData.title || gameData.name,
                incomplete: true
              }
            })
          });
        } catch (error) {
          console.error("Nepavyko išsaugoti taškų:", error);
        }
      }
      setPlaying(false);
      setGameData(null);
    };

    if (isInteractiveGame) {
      // Interactive games use DragDropLayout internally (or we wrap them if needed)
      // For now, we render them directly as they handle their own layout
      return (
        <div className="bg-white">
          {renderGameContent()}
          {globalFeedback && (
            <div className="fixed inset-0 flex items-center justify-center z-[1000] pointer-events-none">
              <div className={`p-4 md:p-16 rounded-2xl md:rounded-[4rem] shadow-2xl animate-pop-in backdrop-blur-md border-4 ${globalFeedback === 'correct' ? 'bg-emerald-500/90 border-emerald-300' : 'bg-rose-500/90 border-rose-300'}`}>
                <div className="text-9xl mb-4 text-center">{globalFeedback === 'correct' ? '🎉' : '❌'}</div>
                <div className="text-xl md:text-5xl font-black text-white text-center drop-shadow-lg uppercase tracking-tighter">{globalFeedback === 'correct' ? 'PUIKIAI!' : 'KLAIDA!'}</div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Standard games use GameLayout
    return (
      <GameLayout
        title={gameData.title}
        score={score}
        timeSpent={timeSpent}
        onExit={onExit}
        headerExtra={
          <div className="hidden md:inline-block px-4 py-1 bg-indigo-100 rounded-full text-xs font-black text-indigo-600 uppercase tracking-[0.2em] mt-1">
            {gameData.difficulty || 'Normal'}
          </div>
        }
      >
        {renderGameContent()}

        {globalFeedback && (
          <div className="fixed inset-0 flex items-center justify-center z-[1000] pointer-events-none">
            <div className={`p-4 md:p-16 rounded-2xl md:rounded-[4rem] shadow-2xl animate-pop-in backdrop-blur-md border-4 ${globalFeedback === 'correct' ? 'bg-emerald-500/90 border-emerald-300' : 'bg-rose-500/90 border-rose-300'}`}>
              <div className="text-9xl mb-4 text-center">{globalFeedback === 'correct' ? '🎉' : '❌'}</div>
              <div className="text-xl md:text-5xl font-black text-white text-center drop-shadow-lg uppercase tracking-tighter">{globalFeedback === 'correct' ? 'PUIKIAI!' : 'KLAIDA!'}</div>
            </div>
          </div>
        )}
      </GameLayout>
    );
  }

  // Selection screen
  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-50 via-white to-indigo-50/30 p-4 md:p-4 md:p-12">
      <div className="max-w-7xl mx-auto">

        {/* Back button */}
        <div className="mb-12">
          <button
            onClick={() => navigate(-1)}
            className="premium-button-3d px-8 py-4 bg-white text-slate-700 rounded-2xl border-slate-200 font-black flex items-center gap-3 group"
          >
            <span className="group-hover:-translate-x-1 transition-transform">←</span> Grįžti į Dashboard
          </button>
        </div>

        <div className="text-center mb-20 relative">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 text-[15rem] opacity-[0.03] font-black select-none pointer-events-none">
            GAMES
          </div>
          <h1 className="text-7xl font-black text-slate-900 mb-6 tracking-tight drop-shadow-sm">
            {category === 'simple' ? '🧩 Paprasti Žaidimai' :
              category === 'advanced' ? '🚀 Sudėtingesni Žaidimai' :
                '🤖 AI Žaidimų Kambarys'}
          </h1>
          <p className="text-2xl text-slate-500 font-bold max-w-2xl mx-auto">
            Visi žaidimai generuojami realiu laiku naudojant stebuklingą Gemini AI! ✨
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:p-8 lg:gap-4 md:p-10">
          {availableGames.map((game) => (
            <div
              key={game.type}
              className={`group relative premium-glass rounded-xl md:rounded-[3rem] overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:shadow-[0_40px_80px_rgba(0,0,0,0.1)] border-white/50 ${isLocked(game.type) ? 'opacity-70 grayscale-[0.5]' : ''}`}
            >
              {isLocked(game.type) && (
                <div className="absolute top-2 md:p-6 right-6 bg-white/80 backdrop-blur-md rounded-2xl w-12 h-12 flex items-center justify-center shadow-lg z-20 border border-white">
                  <span className="text-2xl">🔒</span>
                </div>
              )}

              <div className={`bg-gradient-to-br ${game.color} p-4 md:p-10 text-white text-center relative overflow-hidden h-64 flex flex-col items-center justify-center`}>
                <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" />

                <div className={`text-[7rem] mb-4 transition-transform duration-500 group-hover:scale-125 group-hover:rotate-6 drop-shadow-2xl ${isLocked(game.type) ? 'filter blur-[2px]' : 'animate-float'}`}>
                  {game.icon}
                </div>
                <h3 className="text-base md:text-3xl font-black drop-shadow-md tracking-tight leading-none">{game.name}</h3>
              </div>

              <div className="p-3 md:p-8">
                <p className="text-slate-500 font-bold text-center mb-8 h-12 leading-tight">{game.description}</p>
                <div className="space-y-4">
                  <button
                    onClick={() => {
                      if (isLocked(game.type)) {
                        setRequiredLevel(GAME_ACCESS_LEVELS[game.type] || 1);
                        setShowUpgradeModal(true);
                      } else {
                        setSelectedGame(game);
                        generateGame(game.type, 'easy');
                      }
                    }}
                    className={`premium-button-3d w-full py-5 ${isLocked(game.type) ? 'bg-slate-100 text-slate-400 border-slate-200' : `bg-gradient-to-r ${game.color} text-white border-white/20`} rounded-[1.5rem] font-black text-xl shadow-xl`}
                  >
                    {isLocked(game.type) ? 'Atrakinti' : '🎮 ŽAISTI'}
                  </button>

                  <div className="grid grid-cols-3 gap-2">
                    {['easy', 'medium', 'hard'].map((diff) => (
                      <button
                        key={diff}
                        onClick={() => {
                          if (isLocked(game.type)) {
                            setShowUpgradeModal(true);
                          } else {
                            generateGame(game.type, diff);
                          }
                        }}
                        className={`
                            py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border-b-4
                            ${diff === 'easy' ? 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100' :
                            diff === 'medium' ? 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100' :
                              'bg-rose-50 text-rose-600 border-rose-200 hover:bg-rose-100'}
                          `}
                      >
                        {diff === 'easy' ? 'Lengva' : diff === 'medium' ? 'Vidut.' : 'Sunki'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="mt-32 premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-16 border-white/40 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/5 rounded-full blur-[100px]" />
          <h2 className="text-xl md:text-5xl font-black text-center text-slate-800 mb-16 tracking-tight relative z-10">
            🤖 Ateities Žaidimai Šiandien
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:p-16 relative z-10">
            <div className="text-center group">
              <div className="text-[5rem] mb-6 transition-transform group-hover:scale-125 group-hover:-rotate-12 duration-500">✨</div>
              <h3 className="text-2xl font-black text-slate-800 mb-4 uppercase tracking-tighter">AI Generuojama</h3>
              <p className="text-lg text-slate-500 font-medium leading-relaxed">Kiekvienas žaidimas unikalus, sukuriamas specialiai tavo lygiui.</p>
            </div>
            <div className="text-center group">
              <div className="text-[5rem] mb-6 transition-transform group-hover:scale-125 duration-500">🎯</div>
              <h3 className="text-2xl font-black text-slate-800 mb-4 uppercase tracking-tighter">Adaptyvūs</h3>
              <p className="text-lg text-slate-500 font-medium leading-relaxed">Uždaviniai tampa sunkesni kartu su tavo pasiekimais.</p>
            </div>
            <div className="text-center group">
              <div className="text-[5rem] mb-6 transition-transform group-hover:scale-125 group-hover:rotate-12 duration-500">🧠</div>
              <h3 className="text-2xl font-black text-slate-800 mb-4 uppercase tracking-tighter">Mokomieji</h3>
              <p className="text-lg text-slate-500 font-medium leading-relaxed">Mokslas tampa nuotykiu, kurį norisi kartoti vėl ir vėl.</p>
            </div>
          </div>
        </div>
      </div>
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          requiredLevel={requiredLevel}
        />
      )}
    </div>
  );
};

export default UniversalAIGames;


